import React, { useEffect, useMemo, useRef, useState } from 'react';
import theme from './RoomInfo.module.scss';
import { OptionalId, RoomDto } from '@/types';
import {
    classnames,
    IconPicker,
    InfoIconWithTooltip,
    SimpleCard,
    ValidatingInput,
    ValidatingTextArea,
    ValidationForm,
    ValidationSummary,
} from '@ygtechnologies/bygr-frontend-component-library';
import { Button } from '@ygtechnologies/rubus-ui';
import { Trans } from '@lingui/react/macro';
import { useGetProjectId, useValidationManager } from '@/utils/hooks';
import { useResidentialUnitsByProjectId } from '@/services/residential-units/residential-units.hooks';
import { ButtonBar } from '@/components/Buttons';
import { t } from '@lingui/core/macro';
import { UnitSelector } from '@/seller/components/UnitSelector';

export interface RoomInfoEditProps {
    room: OptionalId<RoomDto>;
    onChange(newValue: OptionalId<RoomDto>): void;
    onSave(unitsToLink: number[], unitsToUnlink: number[]): Promise<void>;
    onSaveAndCreateNew(unitsToLink: number[], unitsToUnlink: number[]): Promise<void>;
    onCancel(): void;
    allRooms: RoomDto[] | null;
}

export const RoomInfoEdit: React.FC<RoomInfoEditProps> = ({ room, onChange, onSave, onSaveAndCreateNew, onCancel, allRooms }) => {
    const projectId = useGetProjectId();
    const { data: units } = useResidentialUnitsByProjectId(projectId);

    const [linkedUnits, setLinkedUnits] = useState<number[]>([]);
    const [originalLinkedUnits, setOriginalLinkedUnits] = useState<number[]>([]);

    const shouldCreateNewAfterSaveRef = useRef(false);
    const saveButtonRef = useRef<HTMLButtonElement>(null);
    const nameInputRef = useRef<HTMLInputElement>(null);

    const validationManager = useValidationManager(
        useMemo(
            () => [
                {
                    isValid: () => {
                        const name = room.name?.trim();
                        if (!allRooms || !name) return true;
                        const note = (room.labelNote ?? '').trim().toLowerCase();

                        return !allRooms.some(
                            (r) =>
                                r.id !== room.id &&
                                r.name.trim().toLowerCase() === name.toLowerCase() &&
                                (r.labelNote ?? '').trim().toLowerCase() === note
                        );
                    },
                    message: () => (
                        <Trans>A room with this name and internal note already exists. Change the note or pick a different name.</Trans>
                    ),
                    summaryMessage: () => (
                        <Trans>A room with this name and internal note already exists. Change the note or pick a different name.</Trans>
                    ),
                },
            ],
            [allRooms, room.id, room.name, room.labelNote]
        )
    );

    useEffect(() => {
        if (!units) return;
        const initialLinked = units.filter((u) => u.projectTags.some((x) => x.id === room.id)).map((u) => u.id);
        setLinkedUnits(initialLinked);
        setOriginalLinkedUnits(initialLinked);
    }, [units, room.id]);

    const unitsToLink = linkedUnits.filter((id) => !originalLinkedUnits.includes(id));
    const unitsToUnlink = originalLinkedUnits.filter((id) => !linkedUnits.includes(id));

    useEffect(() => {
        if (!room.id) {
            nameInputRef.current?.focus();
        }
    }, [room.id]);

    const handleChange = (changes: Partial<RoomDto>) => onChange({ ...room, ...changes });

    const handleSubmit = async () => {
        const errors = validationManager.validate();
        if (errors.length > 0) return;

        if (shouldCreateNewAfterSaveRef.current) {
            shouldCreateNewAfterSaveRef.current = false;
            await onSaveAndCreateNew(unitsToLink, unitsToUnlink);
        } else {
            await onSave(unitsToLink, unitsToUnlink);
        }
    };

    return (
        <SimpleCard className={theme.roomInfoCard}>
            <ValidationForm validationManager={validationManager} onSubmit={handleSubmit}>
                <h2>{room.id ? <Trans>Edit room</Trans> : <Trans>Create new room</Trans>}</h2>
                <div className={theme.topRow}>
                    <div className={theme.labeledInput}>
                        <label>
                            <Trans>Icon</Trans>
                        </label>
                        <IconPicker selectedIcon={room.icon} onSelect={(icon) => handleChange({ icon })} />
                    </div>

                    <div className={theme.labeledInput}>
                        <label>
                            <Trans>Name</Trans>
                        </label>
                        <ValidatingInput
                            ref={nameInputRef}
                            fieldName={t`Name`}
                            placeholder={t`Bathroom 1`}
                            className={'form-control'}
                            value={room.name || ''}
                            required
                            maxLength={50}
                            onChange={(e) => handleChange({ name: e.target.value })}
                        />
                    </div>
                    <div className={theme.labeledInput}>
                        <label>
                            <Trans>Internal note</Trans>
                            <InfoIconWithTooltip
                                content={
                                    <Trans id="project.rooms.internalNote.tooltip">
                                        A short note shown only to sellers next to the room name (e.g. "40cm tiles").
                                        Never visible to owners.
                                    </Trans>
                                }
                            />
                        </label>
                        <ValidatingInput
                            fieldName={t`Internal note`}
                            placeholder={t`e.g. 40cm tiles`}
                            className={'form-control'}
                            value={room.labelNote || ''}
                            maxLength={100}
                            onChange={(e) => handleChange({ labelNote: e.target.value })}
                        />
                    </div>
                </div>

                <div className={theme.descriptionRow}>
                    <div className={theme.labeledInput}>
                        <label>
                            <Trans>Internal description</Trans>
                        </label>
                        <ValidatingTextArea
                            className={classnames('form-control', theme.textArea)}
                            value={room.sellerDescription}
                            onChange={(e) => handleChange({ sellerDescription: e.target.value })}
                        />
                    </div>
                    <div className={theme.labeledInput}>
                        <label>
                            <Trans>External description / legal notice</Trans>
                        </label>
                        <ValidatingTextArea
                            className={classnames('form-control', theme.textArea)}
                            value={room.buyerDescription}
                            onChange={(e) => handleChange({ buyerDescription: e.target.value })}
                        />
                    </div>
                </div>

                <ValidationSummary />
            </ValidationForm>
            <div className={theme.descriptionRow}>
                <div className={theme.labeledInput}>
                    <label>
                        <Trans>Link units</Trans>
                    </label>
                    <UnitSelector selectedResidentialUnitIds={linkedUnits} onChange={setLinkedUnits} projectId={projectId ?? 0} />
                </div>
            </div>
            <ButtonBar>
                <ButtonBar.Right>
                    <Button variant="tertiary" type="button" onClick={() => onCancel()}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button variant="tertiary" type="button" onClick={() => {
                        shouldCreateNewAfterSaveRef.current = true;
                        saveButtonRef.current?.click();
                    }}>
                        <Trans>Save and create new room</Trans>
                    </Button>
                    <Button ref={saveButtonRef} variant="primary" type="button" onClick={handleSubmit}>
                        <Trans>Save</Trans>
                    </Button>
                </ButtonBar.Right>
            </ButtonBar>
        </SimpleCard>
    );
};
